/**
 * What the upload lanes share (2026-10-08, the 50 GB raise).
 *
 * The chat engine keeps the small lanes (the proxy, ≤ PROXY_LANE_MAX_BYTES, and
 * the single-PUT rescue); the direct, resumable lane that carries every larger
 * file is its own chunk (largeUploadLane.ts, `large-upload`), fetched the first
 * time a file needs it. Both halves need the same few facts — the boundary
 * between them, the generic type, the errors that say why a file was not
 * delivered, and which files their person removed — and those live HERE, in the
 * engine, so neither half imports the other.
 */

/**
 * The proxy lane's real ceiling. The proxy hands the whole file to a Cloud
 * Function as ONE request body, and Cloud Run refuses a body over 32 MiB before
 * the function ever runs (gcsSignedUrls.ts says the same of the web adapter).
 * The lane used to be tried for anything up to MAX_INLINE_BYTES_PER_FILE
 * (300 MB): every file between 32 MB and 300 MB sent up to 32 MB, was refused,
 * and only then fell to the lane that could carry it. Not a product limit — the
 * size of a request — so it lives here and not in constants/uploads.ts.
 */
export const PROXY_LANE_MAX_BYTES = 30 * 1024 * 1024;

/** What a file with no browser-reported type, or a type the storage rules do not list, is stored as. */
export const GENERIC_CONTENT_TYPE = 'application/octet-stream';

/** The stored object is not as long as the file that was picked. Never a delivered file. */
export class UploadSizeMismatchError extends Error {
  readonly code = 'upload/size-mismatch';
  constructor(readonly expectedBytes: number, readonly storedBytes: number) {
    super(`stored object is ${storedBytes} bytes; the file is ${expectedBytes}`);
    this.name = 'UploadSizeMismatchError';
    Object.setPrototypeOf(this, UploadSizeMismatchError.prototype);
  }
}

/** No bytes moved for STALL_GIVE_UP_MS while online (uploadFlowControl.ts). */
export class UploadStalledError extends Error {
  readonly code = 'upload/stalled';
  constructor() {
    super('upload made no progress while online; given up');
    this.name = 'UploadStalledError';
    Object.setPrototypeOf(this, UploadStalledError.prototype);
  }
}

/** The person removed the file; its upload was stopped on purpose. */
export class UploadCancelledError extends Error {
  readonly code = 'upload/cancelled';
  constructor() {
    super('upload cancelled: the file was removed');
    this.name = 'UploadCancelledError';
    Object.setPrototypeOf(this, UploadCancelledError.prototype);
  }
}

/**
 * Which files their person removed, and how to stop each one's transfer.
 * WeakMaps for the reason inFlightByFile is one: read by File identity only,
 * and gone with the File.
 */
const cancelledFiles = new WeakSet<File>();
const cancelers = new WeakMap<File, () => boolean>();

export function markUploadCancelled(file: File): void {
  cancelledFiles.add(file);
}

/** Attached again: it is wanted after all. */
export function clearUploadCancelled(file: File): void {
  cancelledFiles.delete(file);
}

export function isUploadCancelled(file: File | null | undefined): boolean {
  return !!file && cancelledFiles.has(file);
}

/** A lane says how to stop the transfer it is running for `file`. Returns the unregister. */
export function registerUploadCanceler(file: File, cancel: () => boolean): () => void {
  cancelers.set(file, cancel);
  return () => {
    if (cancelers.get(file) === cancel) cancelers.delete(file);
  };
}

/** Stop the transfer registered for `file`, if any. Never throws. */
export function cancelRegisteredUpload(file: File): boolean {
  const cancel = cancelers.get(file);
  if (!cancel) return false;
  try {
    return cancel();
  } catch {
    return false;
  }
}
